import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Typography } from 'app/components';
import { i18n } from 'app/utils/i18n';
import { TagItemShortcuts, TagAction } from '../../TagItemShortcuts/TagItemShortcuts';
import { useAddCurrencyToConfig, useRemoveCurrencyFromConfig, useSetActiveCurrency } from 'app/api/tag-queries/document-taxonomy-config';
import { DocumentTaxonomyConfigCurrency, useCreateUnit, useUnitsByDocument } from 'app/api/tag-queries';
import { AffectedTagsModal } from './modals';
import { UnitModal, UnitSpec } from 'app/components/units/UnitModal';
import { unitLabel, nonConfigUnits, simpleCurrencyCode } from 'app/utils/unit-labels';
import Tippy from '@tippyjs/react';

import './Section.scss';

interface UnitDefaultsSectionProps {
  configId: string;
  documentSuperId: number;
  currencySelections: DocumentTaxonomyConfigCurrency[];
}

interface DeleteTarget {
  id: string;
  currency: string;
  count: number;
}

export const UnitDefaultsSection = ({ configId, documentSuperId, currencySelections }: UnitDefaultsSectionProps) => {
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const history = useHistory();
  const location = useLocation();

  const { mutate: addCurrencyToConfig } = useAddCurrencyToConfig();
  const { mutate: removeCurrencyFromConfig } = useRemoveCurrencyFromConfig();
  const { mutate: setActiveCurrency } = useSetActiveCurrency();
  const { mutateAsync: createUnit } = useCreateUnit();
  const { data: documentUnits } = useUnitsByDocument(documentSuperId);

  const otherUnits = nonConfigUnits(currencySelections, documentUnits);
  const activeCurrency = currencySelections.find((c) => c.isActive)?.currency ?? null;

  const handleSave = async (spec: UnitSpec) => {
    const code = simpleCurrencyCode(spec);
    if (code) {
      if (!currencySelections.some((c) => c.currency === code)) {
        addCurrencyToConfig({ configId, currency: code });
      }
      return;
    }
    await createUnit({ documentSuperId, ...spec });
  };

  const handleDeleteClick = (monetary: DocumentTaxonomyConfigCurrency) => {
    const count = monetary.tagCount ?? 0;
    if (count > 0) {
      setDeleteTarget({ id: monetary.id, currency: monetary.currency, count });
    } else {
      removeCurrencyFromConfig({ configId, currencyId: monetary.id });
    }
  };

  const handleGoToTags = () => {
    if (!deleteTarget) return;
    const overviewUrl = location.pathname.replace(/\/taxonomies$/, '');
    history.push(overviewUrl, {
      tagFilter: { type: 'currency', value: `iso4217:${deleteTarget.currency}`, label: deleteTarget.currency },
    });
    setDeleteTarget(null);
  };

  return (
    <section className="defaults-section">
      <div className="defaults-section__header">
        <Typography variant="body-8" fontWeight="semibold">
          {i18n('tag.units.title')} {i18n('tag.default.label')}
        </Typography>
        <button className="custom-btn" onClick={() => setShowUnitModal(true)}>
          <i className="fa-regular fa-plus" />
          {i18n('tag.units.add')}
        </button>
      </div>

      {currencySelections.length === 0 && otherUnits.length === 0 ? (
        <div className="empty-state">
          <Typography variant="body-7" color="#6c757d">
            {i18n('tag.units.empty')}
          </Typography>
        </div>
      ) : (
        <div className="defaults-list">
          {[...currencySelections]
            .sort((a, b) => Number(b.isActive) - Number(a.isActive))
            .map((monetary) => {
              const count = monetary.tagCount ?? 0;
              const actions: TagAction[] = [{ type: 'delete', onClick: () => handleDeleteClick(monetary) }];

              if (!monetary.isActive) {
                actions.unshift({ type: 'default', onClick: () => setActiveCurrency({ configId, currencyId: monetary.id }) });
              }

              return (
                <div key={monetary.id} className={`default-item ${monetary.isActive ? 'default-item--primary' : ''}`}>
                  <div className="default-item__main">
                    <div className="default-item__indicator">
                      {monetary.isActive && (
                        <Tippy
                          content={
                            <Typography variant="body-5" color="#6c757d" className="defaults-section__tippy-content">
                              {i18n('tag.primary.monetary')}
                            </Typography>
                          }
                        >
                          <i className="fa-solid fa-star fa-xs" />
                        </Tippy>
                      )}
                    </div>

                    <div className="default-item__content">
                      <Typography variant="body-7" fontWeight="semibold">
                        {monetary.currency}
                      </Typography>
                    </div>
                  </div>
                  <div className="default-item__meta">
                    <Typography variant="body-5" color="#547BA2">
                      {count} {i18n('tag.active.tags')}
                    </Typography>
                    <TagItemShortcuts actions={actions} />
                  </div>
                </div>
              );
            })}

          {otherUnits.map((unit) => (
            <div key={unit.id} className="default-item">
              <div className="default-item__main">
                <div className="default-item__indicator" />
                <div className="default-item__content">
                  <Typography variant="body-7" fontWeight="semibold">
                    {unitLabel(unit)}
                  </Typography>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <UnitModal show={showUnitModal} defaultCurrency={activeCurrency} onClose={() => setShowUnitModal(false)} onSave={handleSave} />

      <AffectedTagsModal
        show={!!deleteTarget}
        affectedCount={deleteTarget?.count ?? 0}
        itemLabel={deleteTarget?.currency ?? ''}
        onClose={() => setDeleteTarget(null)}
        onGoToTags={handleGoToTags}
      />
    </section>
  );
};
